import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import { IAdminTask, TaskSubjectKind, useAdminTaskAssigneesQuery } from '../../api/admin';
import { formatDate } from '../../utils/dates';
import TaskAssigneeLabel from './TaskAssigneeLabel';
import TaskDialog from './TaskDialog';
import TaskStatusChip from './TaskStatusChip';
import TaskSubjectLinks from './TaskSubjectLinks';
import TaskTypeChip from './TaskTypeChip';

interface Props {
	tasks: IAdminTask[];
	loading: boolean;
	/** The page's own subject kind: tags of that kind are implied, so only the others are shown. */
	ownKind: TaskSubjectKind;
	/** The list scrolls inside itself past this, so a busy account does not push the page down. */
	maxHeight?: number;
	/** Fill the panel's height (set by its neighbours) and scroll inside it, instead of sizing to the rows. */
	fill?: boolean;
}

const isOpen = (t: IAdminTask) => t.status !== 'done';

/** Open items first, soonest due first; then the trail of done items, newest first. */
function sortTrail(tasks: IAdminTask[]): IAdminTask[] {
	return [...tasks].sort((a, b) => {
		if (isOpen(a) !== isOpen(b)) return isOpen(a) ? -1 : 1;
		if (isOpen(a)) {
			const ad = a.due_date ?? '9999-12-31';
			const bd = b.due_date ?? '9999-12-31';
			if (ad !== bd) return ad < bd ? -1 : 1;
			return (b.updated_at ?? '').localeCompare(a.updated_at ?? '');
		}
		const ac = a.completed_date ?? '';
		const bc = b.completed_date ?? '';
		if (ac !== bc) return bc.localeCompare(ac);
		return b.id - a.id;
	});
}

/** The one date that matters on a trail: when it was done, or when it is due. */
function trailDate(task: IAdminTask): string {
	if (!isOpen(task)) return formatDate(task.completed_date);
	return task.due_date ? `due ${formatDate(task.due_date)}` : '';
}

/**
 * A user's or an account's tasks as a compact vertical trail for a narrow column: chips and
 * date, then the title, then who and why it is here. Edits go through the pencil.
 */
export default function TaskTrailList({ tasks, loading, ownKind, maxHeight = 480, fill = false }: Props) {
	const { data: assignees = [] } = useAdminTaskAssigneesQuery();
	const [editing, setEditing] = useState<IAdminTask | null>(null);
	const rows = useMemo(() => sortTrail(tasks), [tasks]);

	let body: React.ReactNode;
	if (loading) {
		body = <Skeleton variant="rectangular" height={64} />;
	} else if (rows.length === 0) {
		body = (
			<Typography variant="body2" color="text.secondary">
				Nothing tagged to this {ownKind} yet.
			</Typography>
		);
	} else {
		body = rows.map((task, i) => (
				<Box key={task.id} sx={{ py: 1.25, borderTop: i > 0 ? '1px solid rgba(56, 56, 56, 1)' : 0 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<TaskStatusChip status={task.status} />
						<TaskTypeChip type={task.type} />
						<Typography
							variant="caption"
							color={isOpen(task) ? 'text.secondary' : 'text.primary'}
							sx={{ ml: 'auto', whiteSpace: 'nowrap' }}
						>
							{trailDate(task)}
						</Typography>
					</Box>
					<Typography variant="body1" sx={{ my: 0.75 }}>
						{task.title}
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
						{task.assignee_user_id !== null && (
							<TaskAssigneeLabel
								user={{ id: task.assignee_user_id, full_name: task.assignee_name, email: task.assignee_email ?? '' }}
								assignees={assignees}
							/>
						)}
						{task.assignee_user_id !== null && task.subject_kind && task.subject_kind !== ownKind && (
							<Typography variant="body2" color="text.secondary">
								·
							</Typography>
						)}
						<TaskSubjectLinks task={task} hideKind={ownKind} />
						<Tooltip title="Edit task">
							<IconButton size="small" sx={{ ml: 'auto' }} onClick={() => setEditing(task)}>
								<EditIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>
		));
	}

	const dialog = <TaskDialog open={!!editing} onClose={() => setEditing(null)} task={editing} />;

	if (fill) {
		// Absolutely positioned so the list never adds to the row's height: the neighbours set it, the list scrolls.
		return (
			<Box sx={{ position: 'relative', height: '100%', minHeight: 360 }}>
				<Box sx={{ position: 'absolute', inset: '0 -16px', overflowY: 'auto', px: 2 }}>{body}</Box>
				{dialog}
			</Box>
		);
	}
	return (
		<Box sx={{ maxHeight, overflowY: 'auto', mx: -2, px: 2 }}>
			{body}
			{dialog}
		</Box>
	);
}
